import { joinUrl } from '@xing-com/crate-core-assets';
import { Eventual } from '@xing-com/crate-xinglet';
import type { GlobalScope, Runtime } from '@xing-com/crate-xinglet/internal';

declare const globalThis: GlobalScope;

export function tryGetModule<T>(name: string, entry?: string): T | undefined {
  const module = globalThis.crateImports?.[entry ? `${name}/${entry}` : name];

  // oxlint-disable-next-line typescript/consistent-type-assertions
  return module as T | undefined;
}

function hasEqualNode(element: HTMLElement): boolean {
  return Array.from(document.querySelectorAll(element.tagName)).some((node) => {
    return node.isEqualNode(element);
  });
}

async function loadNode(
  factory: (n: number) => HTMLElement,
  url: string,
  retry = 1
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const element = factory(retry);

    if (hasEqualNode(element)) {
      return resolve();
    }

    element.onload = () => {
      resolve();
    };
    element.onerror = () => {
      const error = (): void => {
        reject(
          new Error(`Failed to load "${url}"; Details may be in the dev-tools`)
        );
      };
      if (retry > 0) {
        document.head.removeChild(element);
        loadNode(factory, url, retry - 1)
          .then(resolve)
          .catch(error);
      } else {
        error();
      }
    };

    if (element.tagName === 'LINK') {
      // NOTE: This inserts the new element before styled-component. If
      // querySelector returns null the element would be just added at the end.
      // We need this so that styled-components always override styles from CSS
      // files. The reasoning is that styled-components is more dynamic and
      // might change things as part of the rendering.
      document.head.insertBefore(
        element,
        document.querySelector('style[data-styled]')
      );
    } else {
      document.head.appendChild(element);
    }
  });
}

export async function loadScript(url: string): Promise<void> {
  return loadNode(() => {
    const script = document.createElement('script');
    script.defer = true;
    script.async = true;
    script.src = url;
    return script;
  }, url);
}

export async function loadStyle(url: string): Promise<void> {
  return loadNode(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.type = 'text/css';
    link.href = url;
    return link;
  }, url);
}

export function importModule<T>(
  runtime: Runtime,
  name: string,
  entry = 'main'
): Eventual<T> {
  return Eventual.resolve(tryGetModule<T>(name, entry)).then((module) => {
    if (module) return module;

    const manifest = runtime.manifestMap[name];
    const chunk = entry === 'main' ? manifest.entry : manifest.chunks[entry];

    if (!chunk) {
      throw new Error(`Unknown module chunk name '${entry}'`);
    }

    const url = joinUrl(runtime.config.assetsBaseUrl, chunk);

    if (manifest.esm) {
      return Eventual.resolve(import(/* webpackIgnore: true */ url));
    }

    return Eventual.resolve(loadScript(url)).then(() => {
      const module = tryGetModule<T>(name, entry);

      if (!module) {
        throw new Error(`Failed to import module '${name}'`);
      }

      return module;
    });
  });
}
